import { Injectable, inject } from '@angular/core';
import { Observable, from, firstValueFrom } from 'rxjs';
import { ApiService, ApiResponse } from './api.service';
import { ExtensionDto } from './subscription.service';

export interface SubmissionUploadSasFile {
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface SubmissionUploadSlot {
  blobName: string;
  sasUrl: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface SubmissionAttachment {
  blobName: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface SubmissionPreviewItem {
  blobName: string;
  url: string | null;
  error?: string;
}

export type SubmissionOcrStatus = 'Success' | 'Failed' | 'Unsupported' | 'Forbidden';

export interface SubmissionOcrItem {
  blobName: string;
  fileName: string;
  text: string | null;
  status: SubmissionOcrStatus;
  errorMessage: string | null;
}

export interface InvoiceLine {
  description: string;
  qty: number;
  unitPrice: number;
  amount: number;
}

export interface SubscriptionInvoice {
  id: string;
  tenantId: string;
  businessUnitId: string;
  subscriptionId: string;
  invoiceNo: string;
  periodStart: string;
  periodEnd: string;
  issuedDate: string;
  dueDate: string;
  planCode: string;
  planName: string;
  billingCycle: 'Monthly' | 'Annual';
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  currency: string;
  status: 'Draft' | 'Open' | 'Paid' | 'PartiallyPaid' | 'Void' | 'Overdue';
  paidAt?: string | null;
  voidedAt?: string | null;
  voidReason?: string | null;
  lineItems: InvoiceLine[];
}

export interface SubscriptionPayment {
  id: string;
  tenantId: string;
  businessUnitId: string;
  subscriptionId: string;
  invoiceId: string;
  submissionId?: string | null;
  receiptNo: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  source: 'Manual' | 'Gateway';
  gatewayReference?: string | null;
  paidDate: string;
  recordedByUserId: string;
  notes?: string | null;
}

export interface ManualPaymentRequest {
  subscriptionId: string;
  amount: number;
  paymentMethod: string;
  reference?: string;
  paidDate: string;
  notes?: string;
}

export interface SubmitPaymentRequest {
  requestedPlanId: string;
  billingCycle: 'Monthly' | 'Annual';
  declaredAmount: number;
  paymentMethod: string;
  paymentDate: string;
  referenceNumber?: string;
  notes?: string;
  proofFileIds?: string[];
}

export interface SubscriptionPaymentSubmission {
  id: string;
  tenantId: string;
  businessUnitId: string;
  subscriptionId?: string | null;
  requestedPlanId: string;
  requestedBillingCycle: 'Monthly' | 'Annual';
  declaredAmount: number;
  paymentMethod: string;
  paymentDate: string;
  referenceNumber?: string | null;
  submittedByUserId: string;
  notes?: string | null;
  proofFileIds: string[];
  status: 'PendingReview' | 'Approved' | 'Rejected' | 'Withdrawn';
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  reviewerNotes?: string | null;
  extensionApplied?: ExtensionDto | null;
  resultingPaymentId?: string | null;
  resultingInvoiceId?: string | null;
  createdDate: string;
}

export interface ApproveSubmissionRequest {
  extension: ExtensionDto;
  reviewerNotes?: string;
  postToLedger: boolean;
}

export interface SubmitExemptionRequest {
  reason: string;
  desiredExtensionDays?: number;
}

export interface SubscriptionExemption {
  id: string;
  tenantId: string;
  businessUnitId: string;
  subscriptionId: string;
  requestedByUserId: string;
  reason: string;
  desiredExtensionDays?: number | null;
  status: 'Pending' | 'Approved' | 'Rejected';
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  reviewerNotes?: string | null;
  extensionApplied?: ExtensionDto | null;
  createdDate: string;
}

export interface ApproveExemptionRequest {
  extension: ExtensionDto;
  reviewerNotes?: string;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionBillingService {
  private api = inject(ApiService);

  // ---------- Invoices ----------
  listInvoices(buId: string, status?: string): Observable<any> {
    return this.api.get<SubscriptionInvoice[]>(`business-units/${buId}/invoices`, status ? { status } : undefined);
  }

  getInvoice(invoiceId: string): Observable<any> {
    return this.api.get<SubscriptionInvoice>(`invoices/${invoiceId}`);
  }

  voidInvoice(invoiceId: string, reason?: string): Observable<any> {
    return this.api.post<SubscriptionInvoice>(`invoices/${invoiceId}/void`, { reason });
  }

  recordManualPayment(buId: string, invoiceId: string, req: ManualPaymentRequest): Observable<any> {
    return this.api.post<SubscriptionPayment>(`business-units/${buId}/invoices/${invoiceId}/payments`, req);
  }

  listPayments(buId: string): Observable<any> {
    return this.api.get<SubscriptionPayment[]>(`business-units/${buId}/subscription-payments`);
  }

  // ---------- Submissions ----------
  submitPayment(buId: string, req: SubmitPaymentRequest): Observable<any> {
    return this.api.post<SubscriptionPaymentSubmission>(`business-units/${buId}/subscription/submit-payment`, req);
  }

  requestSubmissionUploadSas(
    buId: string,
    files: SubmissionUploadSasFile[]
  ): Observable<ApiResponse<{ uploads: SubmissionUploadSlot[] }>> {
    return this.api.post<{ uploads: SubmissionUploadSlot[] }>(
      `business-units/${buId}/subscription/submission-uploads/upload-sas`,
      { files }
    );
  }

  getSubmissionPreviewSas(
    buId: string,
    blobNames: string[]
  ): Observable<ApiResponse<{ items: SubmissionPreviewItem[] }>> {
    return this.api.post<{ items: SubmissionPreviewItem[] }>(
      `business-units/${buId}/subscription/submission-uploads/preview-sas`,
      { blobNames }
    );
  }

  getAdminSubmissionPreviewSas(
    submissionId: string,
    blobNames: string[]
  ): Observable<ApiResponse<{ items: SubmissionPreviewItem[] }>> {
    return this.api.post<{ items: SubmissionPreviewItem[] }>(
      `admin/subscription-submissions/${submissionId}/preview-sas`,
      { blobNames }
    );
  }

  getSubmissionOcrAmounts(
    submissionId: string,
    blobNames?: string[]
  ): Observable<ApiResponse<{ items: SubmissionOcrItem[] }>> {
    return this.api.post<{ items: SubmissionOcrItem[] }>(
      `admin/subscription-submissions/${submissionId}/ocr-amounts`,
      { blobNames: blobNames ?? [] }
    );
  }

  uploadSubmissionReceipts(buId: string, files: File[]): Observable<ApiResponse<SubmissionAttachment[]>> {
    return from(this.directUploadReceipts(buId, files));
  }

  private async directUploadReceipts(buId: string, files: File[]): Promise<ApiResponse<SubmissionAttachment[]>> {
    if (files.length === 0) {
      return { success: true, data: [] } as ApiResponse<SubmissionAttachment[]>;
    }

    const sasRequest: SubmissionUploadSasFile[] = files.map(f => ({
      fileName: f.name,
      contentType: f.type || 'application/octet-stream',
      sizeBytes: f.size
    }));

    const sasResponse = await firstValueFrom(this.requestSubmissionUploadSas(buId, sasRequest));
    if (!sasResponse.success || !sasResponse.data?.uploads?.length) {
      return { success: false, message: sasResponse.message || 'Failed to obtain upload URLs.' } as ApiResponse<SubmissionAttachment[]>;
    }

    const uploads = sasResponse.data.uploads;
    const attachments: SubmissionAttachment[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const slot = uploads[i];
      const res = await fetch(slot.sasUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': slot.contentType
        },
        body: file
      });
      if (!res.ok) {
        return {
          success: false,
          message: `Upload failed for "${file.name}" (HTTP ${res.status}).`
        } as ApiResponse<SubmissionAttachment[]>;
      }
      attachments.push({
        blobName: slot.blobName,
        fileName: slot.fileName,
        contentType: slot.contentType,
        sizeBytes: slot.sizeBytes
      });
    }

    return { success: true, data: attachments } as ApiResponse<SubmissionAttachment[]>;
  }

  listSubmissionsForBu(buId: string): Observable<any> {
    return this.api.get<SubscriptionPaymentSubmission[]>(`business-units/${buId}/subscription/submissions`);
  }

  listSubmissionQueue(status?: string): Observable<any> {
    return this.api.get<SubscriptionPaymentSubmission[]>('admin/subscription-submissions', status ? { status } : undefined);
  }

  countPendingSubmissions(): Observable<any> {
    return this.api.get<{ count: number }>('admin/subscription-submissions/count');
  }

  getSubmission(id: string): Observable<any> {
    return this.api.get<SubscriptionPaymentSubmission>(`admin/subscription-submissions/${id}`);
  }

  approveSubmission(id: string, req: ApproveSubmissionRequest): Observable<any> {
    return this.api.post<SubscriptionPaymentSubmission>(`admin/subscription-submissions/${id}/approve`, req);
  }

  rejectSubmission(id: string, reviewerNotes?: string): Observable<any> {
    return this.api.post<SubscriptionPaymentSubmission>(`admin/subscription-submissions/${id}/reject`, { reviewerNotes });
  }

  // ---------- Exemptions ----------
  requestExemption(buId: string, req: SubmitExemptionRequest): Observable<any> {
    return this.api.post<SubscriptionExemption>(`business-units/${buId}/subscription/request-exemption`, req);
  }

  listExemptionsForBu(buId: string): Observable<any> {
    return this.api.get<SubscriptionExemption[]>(`business-units/${buId}/subscription/exemptions`);
  }

  listExemptionQueue(status?: string): Observable<any> {
    return this.api.get<SubscriptionExemption[]>('admin/subscription-exemptions', status ? { status } : undefined);
  }

  countPendingExemptions(): Observable<any> {
    return this.api.get<{ count: number }>('admin/subscription-exemptions/count');
  }

  getExemption(id: string): Observable<any> {
    return this.api.get<SubscriptionExemption>(`admin/subscription-exemptions/${id}`);
  }

  approveExemption(id: string, req: ApproveExemptionRequest): Observable<any> {
    return this.api.post<SubscriptionExemption>(`admin/subscription-exemptions/${id}/approve`, req);
  }

  rejectExemption(id: string, reviewerNotes?: string): Observable<any> {
    return this.api.post<SubscriptionExemption>(`admin/subscription-exemptions/${id}/reject`, { reviewerNotes });
  }
}
